import React from "react";
import { useParams } from "react-router-dom";
import type { ModuleLabBuilderContext } from "./module-lab";
import { onAuthStateChanged, removeOnAuthStateChanged } from "../lib/firebase-auth";
import { getCurrentUser } from "@glide/common-core/dist/js/utility/material-app-facilities";
import { logError } from "@glide/support";

type BuilderHookValue = {
    builderContext: ModuleLabBuilderContext;
    isDevMode: boolean;
    setIsDevMode: React.Dispatch<React.SetStateAction<boolean>>;
    selectedModule: string | null;
    setSelectedModule: React.Dispatch<React.SetStateAction<string | null>>;
    sha: string | undefined;
};

interface ModuleLabParams {
    orgID?: string;
    appID?: string;
    module?: string;
    sha?: string;
}

export const useModuleLabBuilder = (): BuilderHookValue => {
    const { orgID, appID, module, sha } = useParams<ModuleLabParams>();
    const [builderContext, setBuilderContext] = React.useState<ModuleLabBuilderContext>({
        orgID,
        appID,
    });
    const [selectedModule, setSelectedModule] = React.useState<string | null>(null);
    const [isDevMode, setIsDevMode] = React.useState<boolean>(false);
    const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
    const hasTokenRef = React.useRef<boolean>(false);

    React.useEffect(() => {
        const updateBuilderContext = async () => {
            try {
                const user = await getCurrentUser();
                const firebaseUserToken = user !== undefined ? await user.getIdToken() : undefined;

                if (firebaseUserToken !== undefined && !hasTokenRef.current) {
                    hasTokenRef.current = true;
                    setBuilderContext(prev => ({
                        ...prev,
                        firebaseUserToken,
                    }));

                    if (pollIntervalRef.current !== null) {
                        clearInterval(pollIntervalRef.current);
                        pollIntervalRef.current = null;
                    }
                }
            } catch (error: unknown) {
                logError("Error fetching user token:", error);
            }
        };

        const authStateChangedCallback = () => {
            void updateBuilderContext();
        };

        onAuthStateChanged(authStateChangedCallback);

        // Polling fallback - check auth state
        pollIntervalRef.current = setInterval(() => {
            void updateBuilderContext();
        }, 1000);

        void updateBuilderContext();

        return () => {
            removeOnAuthStateChanged(authStateChangedCallback);
            if (pollIntervalRef.current !== null) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    React.useEffect(() => {
        setBuilderContext(prev => ({
            ...prev,
            orgID,
            appID,
        }));
    }, [orgID, appID]);

    return {
        builderContext,
        selectedModule: module ?? selectedModule,
        setSelectedModule,
        isDevMode,
        setIsDevMode,
        sha,
    };
};
